import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

const InterviewContinuationBanner = () => {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [incompleteInterview, setIncompleteInterview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkIncompleteInterviews();
  }, []);

  const checkIncompleteInterviews = async () => {
    try {
      // Use SessionManager to get candidate session
      const candidateSession = SessionManager.getSession('candidate');
      if (!candidateSession || !candidateSession.user_id) {
        setLoading(false);
        return;
      }

      const candidateId = candidateSession.user_id;

      // Fetch candidate's applications
      const response = await fetch(`${API_BASE_URL}/candidates/${candidateId}/applications`);

      if (response.ok) {
        const data = await response.json();
        const applications = data.applications || [];
        
        // Find application with interview in progress
        // (has interview_id but status is not interview_completed)
        const inProgressApp = applications.find(app => {
          const hasInterviewId = app.interview_details?.interview_id;
          const notCompleted = app.status !== 'interview_completed' && 
                               app.status !== 'completed' &&
                               app.status !== 'rejected';
          return hasInterviewId && notCompleted;
        });

        if (inProgressApp) {
          console.log('🔄 Found incomplete interview:', inProgressApp);
          setIncompleteInterview(inProgressApp);
          
          // Show banner if not dismissed
          const dismissedKey = `interview_banner_dismissed_${inProgressApp.id}`;
          const dismissed = localStorage.getItem(dismissedKey);
          if (!dismissed) {
            setShowBanner(true);
          }
        }
      }
    } catch (error) {
      console.error('Error checking incomplete interviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueInterview = () => {
    if (!incompleteInterview) return;

    // Navigate to assessment welcome page with job and candidate info
    navigate(`/assessment/welcome?candidate_id=${incompleteInterview.candidate_id}&job_id=${incompleteInterview.job_id}`, {
      state: {
        applicationId: incompleteInterview.id,
        jobTitle: incompleteInterview.job_title,
        candidateName: incompleteInterview.candidate_name,
        continueInterview: true
      }
    });
  };

  const handleDismiss = () => {
    if (!incompleteInterview) return;
    
    setShowBanner(false);
    const dismissedKey = `interview_banner_dismissed_${incompleteInterview.id}`;
    localStorage.setItem(dismissedKey, 'true');
  };

  if (loading || !showBanner || !incompleteInterview) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-1">
            <Icon name="play_circle" size={24} className="text-white hidden sm:block animate-pulse" />
            <div className="flex-1">
              <p className="font-semibold text-sm sm:text-base">
                You have an incomplete interview for <strong>{incompleteInterview.job_title}</strong>
              </p>
              <p className="text-xs sm:text-sm text-orange-100 mt-1">
                Continue from where you left off • Your progress has been saved
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleContinueInterview}
              className="bg-white text-orange-600 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-orange-50 transition-colors flex items-center gap-2"
            >
              <Icon name="play_arrow" size={18} />
              Continue Interview
            </button>
            <button
              onClick={handleDismiss}
              className="text-white hover:text-orange-100 transition-colors"
              aria-label="Dismiss"
            >
              <Icon name="close" size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewContinuationBanner;
